import { SUBSCRIPTION_STATUS, type Account } from '@adtrackify/at-tracking-event-types';

import { getRedirectForStep } from './get-redirect-for-step';
import {
  getResumeRoute,
} from './get-resume-route';
import { BILLING_DESTINATION_LEGACY } from '@/lib/navigation/constants/billing-destination-constants';
import { isAccountEffectivelyActive } from '@/lib/helpers/account/account';
import {
  getStoredProgressForAccount,
} from '@/features/signup/stores/use-signup-flow-store';
import {
  ResolveIncompleteSignupDestinationOptions,
  IncompleteSignupDestination,
} from '../types/resolve-incomplete-signup-destination-types';
import { INCOMPLETE_SIGNUP_DESTINATION_REASON } from '../types/resolve-incomplete-signup-destination-enums';

const MAX_SIGNUP_COMPLETED_STEP = 4;
const SIGNUP_IN_PROGRESS_SUBSCRIPTION_STATUSES = new Set<SUBSCRIPTION_STATUS>([
  SUBSCRIPTION_STATUS.NOT_SUBMITTED,
]);

export function normalizeSignupCompletedStep(step: number): number {
  if (step === 12) return 3;
  if (step === 13) return 4;
  if (step <= 0) return 0;
  if (step >= MAX_SIGNUP_COMPLETED_STEP) return MAX_SIGNUP_COMPLETED_STEP;
  return step;
}

export function isBillingRecoveryAccount(account: Account): boolean {
  const status = account.subscription?.status;
  if (!status) return false;
  if (isAccountEffectivelyActive(account)) return false;
  return !SIGNUP_IN_PROGRESS_SUBSCRIPTION_STATUSES.has(status);
}

function isCancelledSubscription(account: Account): boolean {
  return account.subscription?.status === SUBSCRIPTION_STATUS.CANCELLED;
}

function resolveBillingDestination(account: Account): IncompleteSignupDestination {
  return {
    reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.CANCELLED_SUBSCRIPTION,
    redirectTo: BILLING_DESTINATION_LEGACY,
    account,
  };
}

function resolveSignupDestinationForAccount(
  account: Account,
  options: ResolveIncompleteSignupDestinationOptions
): IncompleteSignupDestination {
  if (isBillingRecoveryAccount(account)) {
    return resolveBillingDestination(account);
  }

  if (options.returnUrl?.startsWith('/signup/')) {
    return {
      reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.RETURN_URL,
      redirectTo: options.returnUrl,
      account,
    };
  }

  const completedStep = normalizeSignupCompletedStep(options.completedStep);
  if (completedStep > 0 && completedStep <= MAX_SIGNUP_COMPLETED_STEP) {
    return {
      reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.RESUME_STEP,
      redirectTo: getRedirectForStep(completedStep),
      account,
    };
  }

  if (options.planId) {
    return {
      reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.PLAN_PAYMENT,
      redirectTo: '/signup/payment',
      account,
      completedStepToSet: 4,
    };
  }

  return {
    reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.DEFAULT_PLANS,
    redirectTo: '/signup/plans',
    account,
    completedStepToSet: 3,
  };
}

function findStoredProgressDestination(
  accounts: Account[],
  userId: string
): IncompleteSignupDestination | undefined {
  for (const account of accounts) {
    const storedProgress = getStoredProgressForAccount(account.id);
    if (storedProgress && storedProgress.userId === userId) {
      const resumeStep = normalizeSignupCompletedStep(storedProgress.completedStep);
      return {
        reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.STORED_PROGRESS,
        redirectTo: getResumeRoute(resumeStep),
        account,
        storedProgress,
      };
    }
  }
  return undefined;
}

export function resolveIncompleteSignupDestination(
  accounts: Account[],
  options: ResolveIncompleteSignupDestinationOptions
): IncompleteSignupDestination {
  if (!accounts.length) {
    return {
      reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.NO_ACCOUNTS,
      redirectTo: '/signup/company',
      completedStepToSet: 2,
    };
  }

  const selectedAccount = options.selectedAccountId
    ? accounts.find(account => account.id === options.selectedAccountId)
    : undefined;

  const incompleteAccounts = accounts.filter(account => !isAccountEffectivelyActive(account));

  // Selected account always wins — classify that workspace only.
  if (selectedAccount) {
    const selectedProgress = getStoredProgressForAccount(selectedAccount.id);
    if (selectedProgress && selectedProgress.userId === options.userId) {
      const resumeStep = normalizeSignupCompletedStep(selectedProgress.completedStep);
      return {
        reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.STORED_PROGRESS,
        redirectTo: getResumeRoute(resumeStep),
        account: selectedAccount,
        storedProgress: selectedProgress,
      };
    }

    return resolveSignupDestinationForAccount(selectedAccount, options);
  }

  const storedProgressDestination = findStoredProgressDestination(
    incompleteAccounts,
    options.userId
  );
  if (storedProgressDestination) {
    return storedProgressDestination;
  }

  if (incompleteAccounts.length > 1) {
    const allCancelled = incompleteAccounts.every(isCancelledSubscription);
    if (allCancelled) {
      // Multi-cancelled without explicit selection: route the first cancelled account.
      const firstCancelled = incompleteAccounts[0];
      if (firstCancelled) {
        return resolveBillingDestination(firstCancelled);
      }
    }

    return {
      reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.MULTIPLE_INCOMPLETE_ACCOUNTS,
      redirectTo: '/signup/resume-error',
      resumeError: {
        type: 'INCOMPLETE_ACCOUNTS',
        accounts: incompleteAccounts,
      },
    };
  }

  const targetAccount = incompleteAccounts[0] ?? accounts[0];
  if (!targetAccount) {
    return {
      reason: INCOMPLETE_SIGNUP_DESTINATION_REASON.NO_ACCOUNTS,
      redirectTo: '/signup/company',
      completedStepToSet: 2,
    };
  }

  return resolveSignupDestinationForAccount(targetAccount, options);
}
